import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { IMenuItemStyles } from '@fluentui/react/lib/ContextualMenu';
import { FontWeights, HighContrastSelector, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { IStyleFunctionOrObject, memoizeFunction } from '@fluentui/react/lib/Utilities';

import {
  commonCalloutBoxShadow,
  commonHighContrastBorder,
  controlPalette,
  getStandardControlSemanticColorsWithTheme,
  IContextualMenuClassNames,
  IMenuItemClassNames,
} from '@1js/acui-common';
import { isThemingEnabled } from '@1js/acui-theme';

const title = (theme: ITheme) => {
  const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    item: {
      backgroundColor: semanticColors.menuBackgroundColor,
    },
    label: {
      color: isThemingEnabled() ? semanticColors.menuItemTextColor : controlPalette.menuItemTextColor,
    },
  };
};
const divider = (theme: ITheme) => {
  const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    divider: {
      margin: '3px 8px',
      backgroundColor: isThemingEnabled() ? semanticColors.menuDividerBackgroundColor : '#E5E5E5',
      display: 'block',
      height: 1,
      position: 'relative',
    },
  };
};

export const getMenuTitleClassNames = memoizeFunction(
  (theme: ITheme): IMenuItemClassNames => {
    return mergeStyleSets(
      {
        divider: {},
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
        screenReaderText: {},
      },
      title(theme)
    );
  }
);

export const getMenuSectionClassNames = memoizeFunction((customDividerStyle?: Partial<IMenuItemStyles>) => {
  return (theme: ITheme): IMenuItemClassNames => {
    return mergeStyleSets(
      {
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
        screenReaderText: {},
      },
      title(theme),
      divider(theme),
      customDividerStyle
    );
  };
});

export const getMenuDividerClassNames = memoizeFunction((customDividerStyle?: Partial<IMenuItemStyles>) => {
  return (theme: ITheme): IMenuItemClassNames => {
    return mergeStyleSets(
      {
        item: {},
        label: {},
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
        screenReaderText: {},
      },
      divider(theme),
      customDividerStyle
    );
  };
});

export const getMenuItemStyles = memoizeFunction(
  (customMenuItemStyle?: Partial<IMenuItemStyles>): Partial<IMenuItemStyles> => {
    return mergeStyleSets(
      {
        item: {},
        label: {},
        divider: {},
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
      },
      customMenuItemStyle
    );
  }
);

export const getMenuClassNames = (customMenuClassNames?: IContextualMenuClassNames) => {
  return memoizeFunction(
    (): IContextualMenuClassNames => {
      // Fabric issue #6922: there's a bug with concatStyleSets and mergeStyles that
      // gives the wrong type for IContextualMenuClassName
      return {
        ...mergeStyleSets(
          {
            container: [
              {
                whiteSpace: 'nowrap',
                paddingTop: 4,
                paddingBottom: 4,
                selectors: {
                  ':focus': { outline: 0 },
                },
              },
              commonHighContrastBorder,
            ],
            list: {
              listStyleType: 'none',
              margin: 0,
              padding: 0,
              lineHeight: 0,
            },
            // Styles to apply to both menu section titles and the menu title
            header: {
              fontSize: 14,
              fontWeight: FontWeights.semibold,
              padding: '0px 12px',
              userSelect: 'none',
              height: 32,
              lineHeight: 32,
              cursor: 'default',
            },
            root: { overflow: 'hidden' },
            title: {},
          },
          customMenuClassNames
        ),
        subComponentStyles: { menuItem: {}, callout: {} },
      };
    }
  );
};

export type GetMenuCalloutStylesResult = IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles>;

export const getMenuCalloutStyles: (
  theme: ITheme,
  customMenuClassNames?: IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles>
) => IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles> = memoizeFunction(
  (
    theme: ITheme,
    customMenuClassNames?: IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles>
  ): IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles> => {
    const backgroundColor = getStandardControlSemanticColorsWithTheme(theme).menuBackgroundColor;
    const calloutStyles: Partial<ICalloutContentStyles> = {
      root: [commonCalloutBoxShadow, { border: 'none' }],
      calloutMain: [
        {
          // The Fabric ContextualMenu explicitly removes 2px from the width of the callout
          // to account for the border. Since our menu doesn't have a border in non high contrast mode, we need to add those 2px back.
          width: 'calc(100% + 2px)',
          selectors: {
            // Since our menu does have border in High Contrast  mode, we don't add 2px back in High Contrast mode.
            [HighContrastSelector]: {
              width: '100%',
            },
          },
        },
        isThemingEnabled() && { backgroundColor },
      ],
    };
    const mergedStyles: GetMenuCalloutStylesResult = (
      props: ICalloutContentStyleProps
    ): Partial<ICalloutContentStyles> => {
      return mergeStyleSets(
        typeof customMenuClassNames === 'function' ? customMenuClassNames(props) : customMenuClassNames,
        calloutStyles
      );
    };
    return mergedStyles;
  }
);
